/** 问题单附件：从脱敏 accessUrl 解析 objectKey、判断预览类型、展示文件名 */

export type IssueAttachmentPreviewKind = 'pdf' | 'image' | 'word' | 'video' | 'none';

export const ISSUE_ATTACHMENT_MAX_BYTES = 20 * 1024 * 1024;
export const ISSUE_VIDEO_MAX_BYTES = 100 * 1024 * 1024;

export function issueAttachmentFullUrl(url: string): string {
  const t = String(url ?? '').trim();
  if (!t) return '';
  if (/^https?:\/\//i.test(t)) return t;
  const base = typeof window !== 'undefined' ? window.location.origin : '';
  return t.startsWith('/') ? base + t : `${base}/${t.replace(/^\//, '')}`;
}

export function isImageAttachmentUrl(url: unknown): boolean {
  const name = issueAttachmentFileName(String(url ?? ''));
  if (/\.(png|jpe?g|gif|webp|bmp|svg|ico)(\?|#|$)/i.test(name)) return true;
  const t = String(url ?? '').trim().toLowerCase();
  if (!t || t === '—') return false;
  if (/\.(png|jpe?g|gif|webp|bmp|svg)(\?|#|$)/i.test(t)) return true;
  return /\/image\//i.test(t) || t.includes('content-type=image');
}

export function isVideoAttachmentUrl(url: unknown): boolean {
  const name = issueAttachmentFileName(String(url ?? ''));
  if (/\.(mp4|webm|ogg|ogv|mov|m4v)(\?|#|$)/i.test(name)) return true;
  const t = String(url ?? '').trim().toLowerCase();
  return /\.(mp4|webm|ogg|ogv|mov|m4v)(\?|#|$)/i.test(t) || t.includes('content-type=video');
}

/** 从脱敏 accessUrl 路径中提取 JWT token 段 */
export function extractFileAccessTokenFromUrl(accessUrl: string): string | null {
  const raw = String(accessUrl ?? '').trim();
  if (!raw) return null;
  try {
    const u = new URL(issueAttachmentFullUrl(raw));
    const m =
      u.pathname.match(/\/api\/files\/access\/([^/?#]+)/i) ||
      u.pathname.match(/\/files\/access\/([^/?#]+)/i);
    if (!m?.[1]) return null;
    return decodeURIComponent(m[1]);
  } catch {
    return null;
  }
}

/** JWT payload 段 UTF-8 安全解码（atob 会破坏中文等非 Latin-1 字符） */
function decodeJwtPayloadJson<T>(payloadSegment: string): T | null {
  try {
    const b64 = payloadSegment.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const binary = atob(padded);
    const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
    const json = new TextDecoder('utf-8').decode(bytes);
    return JSON.parse(json) as T;
  } catch {
    return null;
  }
}

export function extractObjectKeyFromFileAccessUrl(accessUrl: string): string | null {
  const token = extractFileAccessTokenFromUrl(accessUrl);
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length < 2) return null;
  const payload = decodeJwtPayloadJson<{ typ?: string; ok?: string }>(parts[1]);
  if (!payload || payload.typ !== 'file_access') return null;
  const ok = String(payload.ok ?? '').trim();
  return ok || null;
}

export function issueAttachmentFileName(url: string): string {
  const objectKey = extractObjectKeyFromFileAccessUrl(url);
  if (objectKey) {
    const seg = objectKey.split('/').filter(Boolean).pop();
    if (seg) return decodeURIComponent(seg);
  }
  const t = String(url ?? '').trim();
  if (!t) return '附件';
  try {
    const u = new URL(issueAttachmentFullUrl(t));
    const seg = u.pathname.split('/').filter(Boolean).pop();
    if (seg && !seg.includes('.')) {
      /* JWT token segment — fall through */
    } else if (seg) {
      return decodeURIComponent(seg);
    }
  } catch {
    /* ignore */
  }
  if (t.length > 36) return `${t.slice(0, 34)}…`;
  return t;
}

export function isIssueDocumentPreviewable(url: string): boolean {
  const name = issueAttachmentFileName(url).toLowerCase();
  return /\.(pdf|docx?|xlsx?|xls)$/.test(name);
}

export function issueAttachmentPreviewKind(name: string, url: string): IssueAttachmentPreviewKind {
  const candidate = String(name || issueAttachmentFileName(url)).trim().toLowerCase();
  if (/\.pdf$/.test(candidate)) return 'pdf';
  if (/\.(png|jpe?g|gif|webp|bmp|svg)$/.test(candidate)) return 'image';
  if (/\.(doc|docx)$/.test(candidate)) return 'word';
  if (/\.(mp4|webm|ogg|ogv|mov|m4v)$/.test(candidate)) return 'video';
  if (isImageAttachmentUrl(url)) return 'image';
  if (isVideoAttachmentUrl(url)) return 'video';
  return 'none';
}

export function issueAttachmentMaxBytes(name: string, url: string): number {
  return issueAttachmentPreviewKind(name, url) === 'video'
    ? ISSUE_VIDEO_MAX_BYTES
    : ISSUE_ATTACHMENT_MAX_BYTES;
}

export function issueAttachmentFileIcon(url: string): string {
  const name = issueAttachmentFileName(url).toLowerCase();
  if (/\.(mp4|webm|ogg|ogv|mov|m4v)$/.test(name)) return '视频';
  if (name.endsWith('.pdf')) return 'PDF';
  if (/\.(doc|docx)$/.test(name)) return 'DOC';
  if (/\.(xls|xlsx)$/.test(name)) return 'XLS';
  if (/\.(ppt|pptx)$/.test(name)) return 'PPT';
  if (/\.(zip|rar|7z)$/.test(name)) return 'ZIP';
  return '文件';
}

export function sanitizeIssueDownloadFileName(name: string): string {
  const base = (name || 'download').replace(/[/\\?*:"<>|]/g, '_').trim();
  return base || 'download';
}
